import { de, enUS } from 'date-fns/locale'
import { formatDistanceStrict } from 'date-fns'
import React from 'react'
import i18next from 'i18next'

interface FormatLocale {
  localeCode: string
  dateLocale: Locale
  currencyFormat: Intl.NumberFormat
  kmFormat: Intl.NumberFormat
  simpleFormat: Intl.NumberFormat
  timeSuffix: string
}

const createLocale = (
  languageCode: string,
  countryCode: string,
  dateLocale: Locale,
  timeSuffix: string
): FormatLocale => {
  const localeCode = `${languageCode}-${countryCode}`

  const currencyFormat = new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const kmFormat = new Intl.NumberFormat(localeCode, {
    style: 'decimal',
  })

  const simpleFormat = new Intl.NumberFormat(localeCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

  return {
    localeCode,
    dateLocale,
    currencyFormat,
    kmFormat,
    simpleFormat,
    timeSuffix,
  }
}

const localesDictionary: { [localeCode: string]: FormatLocale } = {}
localesDictionary['de'] = createLocale('de', 'DE', de, ' Uhr')
localesDictionary['en'] = createLocale('en', 'US', enUS, '')

const getLocale = (): FormatLocale => {
  return i18next.language in localesDictionary
    ? localesDictionary[i18next.language]
    : localesDictionary['de']
}

export const formatPrice = (price: number) =>
  getLocale().currencyFormat.format(price)

const hasTime = (time: string) => {
  const date = new Date(time)
  return date.getHours() + date.getMinutes() + date.getSeconds() > 0
}

export const formatTime = (date: string) => {
  const locale = getLocale()
  return (
    new Date(date).toLocaleTimeString(locale.localeCode, {
      hour: '2-digit',
      minute: '2-digit',
    }) + locale.timeSuffix
  )
}

export const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString(getLocale().localeCode, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export const formatDateTime = (date: string, hideZeroTime: boolean = true) => {
  if (hideZeroTime && !hasTime(date)) return formatDate(date)
  else return formatDate(date) + ', ' + formatTime(date)
}

export const formatDuration = (startDate: string, endDate: string) =>
  `${formatDateTime(startDate)} — ${formatDateTime(endDate)}`

export const formatDistanceDays = (startDate: string, endDate: string) => {
  const start = new Date(startDate).setHours(0, 0, 0, 0)
  const end = new Date(endDate).setHours(0, 0, 0, 0)
  return formatDistanceStrict(end, start, {
    unit: 'day',
    locale: getLocale().dateLocale,
  })
}

export const formatKilometers = (kilometers: number) =>
  `${getLocale().kmFormat.format(kilometers)} km`

export const formatVolume = (volume: number) => (
  <>
    {getLocale().simpleFormat.format(volume)} m<sup>3</sup>
  </>
)

export const formatArea = (area: number) => (
  <>
    {getLocale().simpleFormat.format(area)} m<sup>2</sup>
  </>
)

export const formatHtmlDateInput = (dateValue: string | Date) => {
  const date = new Date(dateValue)
  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}
